import type { Partner } from './partner-types';
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'preact/hooks';

import PartnerListCard from './PartnerListCard';

type Props = {
	allPartners: Partner[];
	show: boolean;
};

const KEYWORD_MIN_LENGTH = 2;
const PER_PAGE = 50;

export default function PartnerList({ allPartners, show }: Props) {
	const [keyword, setKeyword] = useState(getInitialKeyword());

	/* Manage focus after inserting new elements
	 https://www.aleksandrhovhannisyan.com/blog/load-more-button-focus/
	 https://tkdodo.eu/blog/avoiding-use-effect-with-callback-refs */
	const firstInsertedIndex = useRef<null | number>(null);
	const [visible, setVisible] = useState(PER_PAGE);
	const focusCallback = useCallback((node: HTMLElement | null) => {
		console.log(node);
		if (node) node.focus();
	}, []);

	const showMore = useCallback(() => {
		setVisible(visible + PER_PAGE);
		firstInsertedIndex.current = visible; // E.g. 10 focuses the 11th element, which has index 10
	}, [visible]);

	useEffect(() => {
		const url = new URL(window.location.href);
		if (keyword.length < KEYWORD_MIN_LENGTH) {
			url.searchParams.delete('keyword');
		} else {
			url.searchParams.set('keyword', keyword);
		}

		window.history.replaceState({}, '', url.toString());
		firstInsertedIndex.current = null;
		setVisible(PER_PAGE);
	}, [keyword]);

	// https://html.spec.whatwg.org/multipage/grouping-content.html#the-search-element

	const filteredPartners = useMemo(
		() => getFilteredPartners(allPartners, keyword),
		[keyword],
	);

	const { regionalPartners, networkPartners } = useMemo(() => {
		// Show regional partners first
		const sortedPartners = [...filteredPartners]
			.sort((a, b) => {
				if (a.type === b.type) return 0;
				if (a.type === 'network' && b.type === 'regional') return 1;
				return 1;
			})
			.slice(0, visible);

		const regionalPartners = sortedPartners.filter(
			({ type }) => type === 'regional',
		);
		const networkPartners = sortedPartners.filter(
			({ type }) => type === 'network',
		);

		return { regionalPartners, networkPartners };
	}, [filteredPartners, visible]);

	return (
		<search hidden={!show} aria-label="Partnersuche">
			<div className="filter-button-wrapper">
				<label className="has-heading-4-font-size" htmlFor="filter-keyword">
					Nach Schlagwort durchsuchen:
				</label>
				<div className="wp-block-search">
					<label
						htmlFor="filter-keyword"
						className="wp-block-search__label visually-hidden"
					>
						Suche…
					</label>

					<div className="wp-block-search__inside-wrapper mb-0">
						<input
							type="search"
							className="wp-block-search__input"
							id="filter-keyword"
							value={keyword}
							onChange={(e) => setKeyword(e.currentTarget.value)}
						/>
						<span className="wp-block-search__button btn btn-primary btn-icon">
							<svg className="icon" id="filter-keyword-icon" aria-hidden="true">
								<use xlinkHref="/wp-content/themes/acb-theme/assets/build/icons/sprite.min.svg?bust=1.44.0#icon-search"></use>
							</svg>
						</span>
					</div>
				</div>
			</div>
			<output id="partner-count" className="partner-network__status">
				{getStatusText(filteredPartners.length)}
			</output>
			<section aria-labelledby="partner-count">
				{regionalPartners.length > 0 && <h2>Regionale Partner</h2>}
				{regionalPartners.map((partner: Partner, index) => (
					<PartnerListCard
						key={partner.id}
						layout="list"
						{...partner}
						focusCallback={
							index === firstInsertedIndex.current ? focusCallback : undefined
						}
					/>
				))}{' '}
				{networkPartners.length > 0 && <h2>Netzwerkpartner</h2>}
				{networkPartners.map((partner: Partner, index) => (
					<PartnerListCard
						key={partner.id}
						layout="list"
						{...partner}
						focusCallback={
							index === firstInsertedIndex.current ? focusCallback : undefined
						}
					/>
				))}
				{filteredPartners.length > visible && (
					<button
						onClick={showMore}
						className="wp-block-button__link has-accent-background-color has-background wp-element-button"
					>
						Weitere Partner anzeigen
					</button>
				)}
			</section>
		</search>
	);
}

function getInitialKeyword() {
	const url = new URL(window.location.href);
	return url.searchParams.get('keyword') || '';
}

function getFilteredPartners(allPartners: Partner[], keyword: string) {
	if (keyword.length < KEYWORD_MIN_LENGTH) return allPartners;

	const regExp = new RegExp(keyword, 'i');

	return allPartners.filter(({ title, address: { city, zipcode } }) => {
		return (
			regExp.test(title) ||
			regExp.test(city) ||
			zipcode.toString().startsWith(keyword)
		);
	});
}

function getStatusText(count: number) {
	switch (count) {
		case 0:
			return 'Nichts gefunden.';
		case 1:
			return 'Ein Eintrag gefunden.';
		default:
			return `${count} Einträge gefunden.`;
	}
}
